<template>
    <div class="forgot-password">
        <div class="forgot-password__heading">
            <div class="forgot-password__eyebrow">ACCOUNT RECOVERY</div>
            <div class="forgot-password__title">找回密码</div>
            <div class="forgot-password__subtitle">支持趣拍精灵原账号和趣拍星统一账号</div>
        </div>

        <el-form class="forgot-password__form" :model="formData" @submit.native.prevent>
            <el-form-item>
                <el-input
                    v-model="formData.username"
                    prefix-icon="el-icon-user-solid"
                    placeholder="请输入手机号或邮箱"
                    autocomplete="username">
                </el-input>
            </el-form-item>

            <transition name="forgot-options">
                <div class="forgot-password__options" v-if="accountOptions.length && !formData.account_type">
                    <div class="forgot-password__options-label">请选择要重置的账号</div>
                    <button
                        class="forgot-password__option"
                        v-for="item in accountOptions"
                        :key="item.value"
                        type="button"
                        :disabled="sendingCode"
                        @click="selectAccountType(item)">
                        <span class="forgot-password__option-icon">
                            <i :class="item.value === 'legacy' ? 'el-icon-picture-outline' : 'el-icon-connection'"></i>
                        </span>
                        <span class="forgot-password__option-copy">
                            <span>{{ item.label }}</span>
                            <small>{{ item.value === 'legacy' ? '原趣拍精灵独立密码' : '趣拍星通用登录密码' }}</small>
                        </span>
                        <i class="el-icon-arrow-right"></i>
                    </button>
                </div>
            </transition>

            <div class="forgot-password__selected" v-if="accountTypeLabel">
                <span><i></i>本次找回：{{ accountTypeLabel }}</span>
                <button v-if="accountOptions.length > 1" type="button" @click="changeAccountType">更换</button>
            </div>

            <el-form-item>
                <el-input
                    v-model="formData.code"
                    prefix-icon="el-icon-message"
                    maxlength="8"
                    placeholder="请输入验证码"
                    autocomplete="one-time-code">
                    <el-button slot="append" :loading="sendingCode" :disabled="seconds > 0" @click="sendCode">{{ codeText }}</el-button>
                </el-input>
            </el-form-item>
            <el-form-item>
                <el-input
                    v-model="formData.password"
                    prefix-icon="el-icon-lock"
                    type="password"
                    maxlength="64"
                    placeholder="请输入新密码"
                    autocomplete="new-password"
                    show-password>
                </el-input>
            </el-form-item>
            <el-form-item>
                <el-input
                    v-model="formData.password_confirmation"
                    prefix-icon="el-icon-circle-check"
                    type="password"
                    maxlength="64"
                    placeholder="请再次输入新密码"
                    autocomplete="new-password"
                    show-password
                    @keyup.enter.native="submit">
                </el-input>
            </el-form-item>

            <div class="forgot-password__actions">
                <el-button class="forgot-password__back" plain :disabled="submitting" @click="$emit('back')">返回登录</el-button>
                <el-button class="forgot-password__submit" type="primary" :loading="submitting" @click="submit">重置密码</el-button>
            </div>
        </el-form>
    </div>
</template>

<script>
    import { requestAccountForgot, requestAccountForgotCode } from '@/api/app';

    export default {
        name: 'forgot-password-form',
        props: {
            username: {
                type: String,
                default: ''
            }
        },
        data(){
            return {
                sendingCode: false,
                submitting: false,
                seconds: 0,
                timer: null,
                stateUsername: '',
                codeUsername: '',
                accountOptions: [],
                accountTypeLabel: '',
                formData: {
                    username: this.username || '',
                    code: '',
                    password: '',
                    password_confirmation: '',
                    account_type: ''
                }
            }
        },
        computed: {
            codeText(){
                if(this.sendingCode) return '发送中';
                return this.seconds > 0 ? this.seconds + 's' : '获取验证码';
            }
        },
        watch: {
            'formData.username'(value){
                if(!this.stateUsername || String(value || '').trim() === this.stateUsername) return;
                this.resetCodeState();
            }
        },
        beforeDestroy(){
            this.clearTimer();
        },
        methods: {
            normalizeUsername(){
                return String(this.formData.username || '').trim();
            },
            clearTimer(){
                if(this.timer){
                    clearInterval(this.timer);
                    this.timer = null;
                }
            },
            resetCodeState(){
                this.clearTimer();
                this.seconds = 0;
                this.stateUsername = '';
                this.codeUsername = '';
                this.accountOptions = [];
                this.accountTypeLabel = '';
                this.formData.account_type = '';
                this.formData.code = '';
            },
            startTimer(){
                this.clearTimer();
                this.seconds = 60;
                this.timer = setInterval(() => {
                    this.seconds--;
                    if(this.seconds <= 0){
                        this.seconds = 0;
                        this.clearTimer();
                    }
                }, 1000);
            },
            sendCode(){
                if(this.sendingCode || this.seconds > 0) return;
                const username = this.normalizeUsername();
                if(!username) return this.$utils.showMsg('请输入手机号或邮箱', 'error');
                this.formData.username = username;
                this.requestCode(username, this.formData.account_type);
            },
            requestCode(username, accountType = ''){
                this.sendingCode = true;
                this.stateUsername = username;
                requestAccountForgotCode({
                    username: username,
                    account_type: accountType
                }).then(res => {
                    this.sendingCode = false;
                    if(this.normalizeUsername() !== username) return;
                    const data = res.data || {};
                    if(data.need_select){
                        this.accountOptions = Array.isArray(data.account_options) ? data.account_options : [];
                        this.formData.account_type = '';
                        this.accountTypeLabel = '';
                        return;
                    }
                    this.formData.account_type = data.account_type || accountType;
                    this.accountTypeLabel = data.account_type_label || '';
                    this.codeUsername = username;
                    this.$utils.showMsg(res.message || '验证码发送成功');
                    this.startTimer();
                }).catch(() => {
                    this.sendingCode = false;
                });
            },
            selectAccountType(item){
                if(!item || !item.value || this.sendingCode) return;
                const username = this.normalizeUsername();
                if(!username || username !== this.stateUsername) return this.resetCodeState();
                this.formData.account_type = item.value;
                this.accountTypeLabel = item.label;
                this.requestCode(username, item.value);
            },
            changeAccountType(){
                if(this.seconds > 0){
                    return this.$utils.showMsg('请等待验证码倒计时结束后再更换账号', 'warning');
                }
                this.formData.account_type = '';
                this.accountTypeLabel = '';
                this.codeUsername = '';
                this.formData.code = '';
            },
            submit(){
                if(this.submitting) return;
                const username = this.normalizeUsername();
                if(!username) return this.$utils.showMsg('请输入手机号或邮箱', 'error');
                if(!this.formData.code) return this.$utils.showMsg('请输入验证码', 'error');
                if(!this.formData.account_type || this.codeUsername !== username){
                    return this.$utils.showMsg('请先获取当前账号的验证码', 'error');
                }
                if(!this.formData.password) return this.$utils.showMsg('请输入新密码', 'error');
                if(!this.formData.password_confirmation) return this.$utils.showMsg('请再次输入新密码', 'error');
                if(this.formData.password !== this.formData.password_confirmation){
                    return this.$utils.showMsg('两次密码输入不一致', 'error');
                }

                this.formData.username = username;
                this.submitting = true;
                requestAccountForgot(this.formData).then(res => {
                    this.submitting = false;
                    this.$utils.showMsg(res.message || '密码重置成功');
                    this.$emit('success', username);
                }).catch(() => {
                    this.submitting = false;
                });
            }
        }
    }
</script>

<style lang="scss">
    .forgot-password {
        width: 360px;
        margin: -14px auto 0;
        color: #223043;
        .forgot-password__heading {
            margin-bottom: 16px;
            text-align: center;
        }
        .forgot-password__eyebrow {
            color: rgba(24, 144, 255, 0.72);
            font-size: 10px;
            font-weight: 700;
            letter-spacing: 0.22em;
        }
        .forgot-password__title {
            margin-top: 5px;
            color: $com-color-primary;
            font-size: 27px;
            font-weight: 600;
            letter-spacing: 0.16em;
            text-indent: 0.16em;
        }
        .forgot-password__subtitle {
            margin-top: 7px;
            color: $com-color-minor;
            font-size: 13px;
        }
        .forgot-password__form {
            .el-form-item {
                margin-bottom: 13px;
            }
            .el-input__inner {
                height: 42px;
                border-color: #dce8f5;
                background: #fbfdff;
            }
            .el-input-group__append {
                padding: 0;
                border-color: #dce8f5;
                background: $com-color-primary;
                overflow: hidden;
                .el-button {
                    min-width: 108px;
                    height: 40px;
                    margin: 0;
                    border: 0;
                    border-radius: 0;
                    color: #ffffff;
                    background: $com-color-primary;
                    &.is-disabled {
                        color: rgba(255, 255, 255, 0.72);
                        background: #79baf5;
                    }
                }
            }
        }
        .forgot-password__options {
            margin: -2px 0 13px;
            padding: 10px;
            border: 1px solid #dfeaf6;
            border-radius: 12px;
            background: linear-gradient(145deg, #f8fbff 0%, #eef6ff 100%);
        }
        .forgot-password__options-label {
            margin: 0 2px 8px;
            color: #62758b;
            font-size: 12px;
        }
        .forgot-password__option {
            width: 100%;
            min-height: 48px;
            display: flex;
            align-items: center;
            margin-top: 7px;
            padding: 7px 10px;
            border: 1px solid rgba(24, 144, 255, 0.12);
            border-radius: 10px;
            background: rgba(255, 255, 255, 0.86);
            color: #27384c;
            text-align: left;
            cursor: pointer;
            transition: border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;
            &:hover {
                border-color: rgba(24, 144, 255, 0.62);
                box-shadow: 0 7px 18px rgba(24, 144, 255, 0.1);
                transform: translateY(-1px);
            }
            &:disabled {
                cursor: not-allowed;
                opacity: 0.62;
            }
            > .el-icon-arrow-right {
                color: $com-color-primary;
            }
        }
        .forgot-password__option-icon {
            width: 32px;
            height: 32px;
            flex: 0 0 32px;
            display: flex;
            align-items: center;
            justify-content: center;
            margin-right: 10px;
            border-radius: 9px;
            color: $com-color-primary;
            background: #e8f4ff;
            font-size: 17px;
        }
        .forgot-password__option-copy {
            min-width: 0;
            flex: 1;
            display: flex;
            flex-direction: column;
            font-size: 14px;
            small {
                margin-top: 2px;
                color: #8a9aae;
                font-size: 11px;
            }
        }
        .forgot-password__selected {
            min-height: 32px;
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin: -2px 0 11px;
            padding: 0 10px;
            border-radius: 9px;
            color: #3378ad;
            background: #edf7ff;
            font-size: 12px;
            span > i {
                width: 6px;
                height: 6px;
                display: inline-block;
                margin-right: 7px;
                border-radius: 50%;
                background: #36b37e;
                box-shadow: 0 0 0 4px rgba(54, 179, 126, 0.12);
            }
            button {
                padding: 3px 0 3px 10px;
                border: 0;
                color: $com-color-primary;
                background: transparent;
                cursor: pointer;
            }
        }
        .forgot-password__actions {
            display: flex;
            gap: 10px;
            margin-top: 3px;
            .el-button {
                height: 42px;
                margin: 0;
            }
        }
        .forgot-password__back {
            width: 34%;
        }
        .forgot-password__submit {
            flex: 1;
        }
    }

    .black_mode .forgot-password {
        color: #eef5ff;
        .forgot-password__subtitle,
        .forgot-password__options-label {
            color: rgba(229, 239, 252, 0.62);
        }
        .forgot-password__form .el-input__inner {
            border-color: rgba(255, 255, 255, 0.12);
            background: rgba(255, 255, 255, 0.07);
            color: #f4f8ff;
        }
        .forgot-password__options {
            border-color: rgba(255, 255, 255, 0.1);
            background: rgba(255, 255, 255, 0.04);
        }
        .forgot-password__option {
            border-color: rgba(255, 255, 255, 0.1);
            background: rgba(255, 255, 255, 0.06);
            color: #f2f7ff;
        }
        .forgot-password__selected {
            color: #a9d8ff;
            background: rgba(24, 144, 255, 0.12);
        }
    }

    .forgot-options-enter-active,
    .forgot-options-leave-active {
        transition: opacity 0.2s ease, transform 0.2s ease;
    }
    .forgot-options-enter,
    .forgot-options-leave-to {
        opacity: 0;
        transform: translateY(-6px);
    }
</style>
